import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { vendorApi } from '../../services/api';
import { applyTenantBranding } from '../../utils/portal';
import Sidebar from '../../components/Sidebar';
import TopBar from '../../components/TopBar';

export default function FixedCosts() {
  const navigate = useNavigate();

  const { data: vendorData, isLoading: vendorLoading, error } = useQuery({
    queryKey: ['vendor', 'me'],
    queryFn: async () => {
      const response = await vendorApi.me();
      return response.data;
    },
  });

  const { data: fixedCostsData, isLoading: costsLoading } = useQuery({
    queryKey: ['vendor', 'fixed-costs'],
    queryFn: async () => {
      const response = await vendorApi.getFixedCosts();
      return response.data;
    },
    enabled: !!vendorData,
  });

  useEffect(() => {
    if (error) {
      navigate('/login');
    }
  }, [error, navigate]);

  useEffect(() => {
    if (vendorData?.tenant?.primaryColor) {
      applyTenantBranding(vendorData.tenant.primaryColor);
    }
  }, [vendorData]);

  const handleLogout = async () => {
    try {
      await vendorApi.logout();
      navigate('/login');
    } catch (err) {
      console.error('Logout error:', err);
      navigate('/login');
    }
  };

  if (vendorLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-600">Laddar...</p>
        </div>
      </div>
    );
  }

  // Only costs deducted today: started, not ended, not Inactive (same rules as the settlement run)
  const today = new Date().toISOString().slice(0, 10);
  const isBlankDate = (d?: string) => !d || d.startsWith('0001-');
  const activeCosts = (fixedCostsData?.fixedCosts || []).filter(
    (c: any) =>
      c.type !== 'Inactive' &&
      (isBlankDate(c.activeFromDate) || c.activeFromDate <= today) &&
      (isBlankDate(c.activeToDate) || c.activeToDate >= today)
  );
  const percentageCount = activeCosts.filter((c: any) => c.type === 'Percentage').length;
  const fixedAmountTotal = activeCosts
    .filter((c: any) => c.type !== 'Percentage')
    .reduce((sum: number, c: any) => sum + (c.costPerPeriod || 0), 0);
  const shortDate = (d: string) => new Date(d).toLocaleDateString('sv-SE');
  const periodText = (c: any) =>
    isBlankDate(c.activeToDate)
      ? isBlankDate(c.activeFromDate) ? 'Tills vidare' : `Från ${shortDate(c.activeFromDate)}`
      : `T.o.m. ${shortDate(c.activeToDate)}`;

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <Sidebar
        logoUrl={vendorData?.tenant?.logoUrl}
        companyName={vendorData?.tenant?.companyName}
        onLogout={handleLogout}
      />

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col">
        {/* Top Bar */}
        <TopBar
          userName={vendorData?.vendor?.fullName || vendorData?.vendor?.username}
        />

        {/* Page Content */}
        <main className="mx-auto w-full max-w-[1400px] flex-1 px-6 py-8 lg:px-10 lg:py-10">
          <div className="mb-6">
            <h1 className="font-display text-[1.35rem] leading-snug text-ink text-balance md:text-[1.6rem]">Fasta kostnader</h1>
            <p className="text-gray-600 mt-1">Avdrag som dras vid varje avräkning</p>
          </div>

          {/* Fixed Costs Table: only rows that are deducted today */}
          <div className="card">
              <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Beskrivning
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Resurs
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Gäller
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Avdrag per period
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Dragna perioder
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {costsLoading ? (
                    <tr>
                      <td colSpan={5} className="px-6 py-10 text-center text-gray-500">
                        Laddar fasta kostnader…
                      </td>
                    </tr>
                  ) : activeCosts.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-6 py-10 text-center text-gray-500">
                        Inga fasta kostnader dras just nu
                      </td>
                    </tr>
                  ) : (
                    activeCosts.map((cost: any) => (
                      <tr key={`${cost.resourceNo}-${cost.lineNo}`} className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {cost.description}
                          {cost.type === 'Percentage' && (
                            <span className="ml-2 rounded-md bg-gray-100 px-1.5 py-0.5 text-[11px] font-medium text-gray-600">Procent av frakt</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {cost.resourceNo || 'Alla resurser'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {periodText(cost)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right font-medium">
                          {cost.type === 'Percentage'
                            ? `${(cost.percentage || 0).toLocaleString('sv-SE')} %`
                            : (cost.costPerPeriod || 0).toLocaleString('sv-SE', { style: 'currency', currency: 'SEK' })}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                          {cost.postedCosts || 0}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Summary Footer */}
            {activeCosts.length > 0 && (
              <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
                <div className="flex flex-wrap justify-between items-center gap-2">
                  <span className="text-sm text-gray-600">
                    {activeCosts.length} aktiva avdrag
                    {percentageCount > 0 && ` · ${percentageCount} beräknas i procent av periodens frakt`}
                  </span>
                  <span className="text-sm font-medium text-gray-900">
                    Fasta belopp per period:{' '}
                    {fixedAmountTotal.toLocaleString('sv-SE', { style: 'currency', currency: 'SEK' })}
                  </span>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
